import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import React from "react";
import styled, { css } from "styled-components";
import { LoaderCircle, SlidersHorizontal, X } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout";

interface NewLookFiltersButtonProps {
  open?: boolean;
  numActiveFilters: number;
  loading?: boolean;
  onClick: () => void;
  onReset: () => void;
}

/**
 * New look: the bots "Filters" toggle as a quiet 32px pill (the same family
 * as the sort pill), tinted while open or filtering, with the active count
 * and a "Reset" pill next to it.
 */
const NewLookFiltersButton = ({
  open,
  numActiveFilters,
  loading,
  onClick,
  onReset,
}: NewLookFiltersButtonProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const Icon = loading ? Spinner : SlidersHorizontal;
  return (
    <>
      <FiltersPill
        type="button"
        id="bots-filters"
        onClick={onClick}
        $active={open || numActiveFilters > 0}
        aria-expanded={!!open}
      >
        <Icon size={15} strokeWidth={NEW_LOOK_ICON_STROKE} />
        {t("labels.filters")}
        {numActiveFilters > 0 && <Count>{numActiveFilters}</Count>}
      </FiltersPill>
      {numActiveFilters > 0 && (
        <ResetPill type="button" onClick={onReset}>
          <X size={14} strokeWidth={2} />
          {tCommon("filters.common.reset")}
        </ResetPill>
      )}
    </>
  );
};

export default NewLookFiltersButton;

/** A quiet 32px pill, the same family as the sort control. */
const newLookPill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: ${(p) => p.theme.borders.card};
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  color: ${(p) => p.theme.colors.titleColor};
  font: inherit;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  cursor: pointer;
  transition: color 150ms, background-color 150ms, border-color 150ms;
  svg {
    flex: none;
    color: ${(p) => p.theme.colors.secondaryText};
  }
  &:hover {
    border-color: ${(p) => p.theme.colors.mediumBorderGrey};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.2)};
  }
`;
const FiltersPill = styled.button<{ $active?: boolean }>`
  ${newLookPill};
  ${(p) =>
    p.$active &&
    css`
      border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.45)};
      background: ${p.theme.colors.lightBlue};
      color: ${p.theme.colors.primaryBlue};
      svg {
        color: ${p.theme.colors.primaryBlue};
      }
      &:hover {
        border-color: ${hexToRGB(p.theme.colors.primaryBlue, 0.6)};
        background: ${p.theme.colors.lightBlue};
      }
    `}
`;
const ResetPill = styled.button`
  ${newLookPill};
  padding: 0 10px;
  border-color: transparent;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  &:hover {
    border-color: transparent;
    background: ${(p) => p.theme.colors.cardLightBgColor};
    color: ${(p) => p.theme.colors.titleColor};
  }
`;
const Count = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.primaryBlue};
  color: ${(p) => p.theme.colors.buttonText};
  font-size: 11px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  text-align: center;
  font-variant-numeric: tabular-nums;
`;
const Spinner = styled(LoaderCircle)`
  animation: newLookBotsSpin 0.9s linear infinite;
  @keyframes newLookBotsSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;
